import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { Globe, Shield, Bell, Trash2, KeyRound, Mail, LogOut, ChevronRight, X, Check, Sun, Moon } from 'lucide-react';
import { SUPPORTED_LANGUAGES, changeLanguage } from '../i18n/index.js';
import PasswordInput from '../components/PasswordInput';
import api from '../api/client';

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="glass-card w-full max-w-md mx-4 p-6 relative max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 hover:opacity-70" style={{color:'var(--text-muted)'}}><X size={20} /></button>
        <h2 className="text-lg font-bold mb-4" style={{color:'var(--text-primary)'}}>{title}</h2>
        {children}
      </div>
    </div>
  );
}

export default function Settings() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [showEmailModal, setShowEmailModal] = React.useState(false);
  const [showPasswordModal, setShowPasswordModal] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [showLangModal, setShowLangModal] = React.useState(false);

  const [emailForm, setEmailForm] = React.useState({ new_email: '', password: '' });
  const [passwordForm, setPasswordForm] = React.useState({ current_password: '', new_password: '' });
  const [deletePassword, setDeletePassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [msg, setMsg] = React.useState({ type: '', text: '' });

  const currentLang = SUPPORTED_LANGUAGES.find(l => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setLoading(true); setMsg({});
    try {
      await api.post('/users/change-email', emailForm);
      setMsg({ type: 'success', text: t('common.success') });
      setEmailForm({ new_email: '', password: '' });
      setTimeout(() => { setShowEmailModal(false); setMsg({}); }, 2000);
    } catch (err) { setMsg({ type: 'error', text: err.response?.data?.detail || t('common.error') }); }
    setLoading(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setLoading(true); setMsg({});
    try {
      await api.post('/users/change-password', passwordForm);
      setMsg({ type: 'success', text: t('common.success') });
      setPasswordForm({ current_password: '', new_password: '' });
      setTimeout(() => { setShowPasswordModal(false); setMsg({}); }, 2000);
    } catch (err) { setMsg({ type: 'error', text: err.response?.data?.detail || t('common.error') }); }
    setLoading(false);
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setLoading(true); setMsg({});
    try {
      await api.post('/users/delete-account', { password: deletePassword });
      logout(); navigate('/');
    } catch (err) { setMsg({ type: 'error', text: err.response?.data?.detail || t('common.error') }); }
    setLoading(false);
  };

  const handleLogout = () => { logout(); navigate('/'); };

  const items = [
    { icon: Mail, label: t('settings.changeEmail'), value: user?.email || '', color: 'text-blue-400', onClick: () => { setMsg({}); setShowEmailModal(true); } },
    { icon: KeyRound, label: t('settings.changePassword'), value: '', color: 'text-violet-400', onClick: () => { setMsg({}); setShowPasswordModal(true); } },
    { icon: Globe, label: t('settings.language'), value: currentLang.nativeName, color: 'text-cyan-400', onClick: () => setShowLangModal(true) },
    { icon: theme === 'dark' ? Sun : Moon, label: theme === 'dark' ? 'Mode Jour' : 'Mode Nuit', value: theme === 'dark' ? 'Sombre' : 'Clair', color: 'text-amber-400', onClick: toggleTheme },
    { icon: Bell, label: t('settings.notifications'), value: '', color: 'text-yellow-400' },
    { icon: Shield, label: t('settings.privacy'), value: '', color: 'text-green-400', onClick: () => navigate('/privacy') },
  ];

  return (
    <div className="h-full overflow-y-auto" data-testid="settings-page">
      <header className="px-8 py-6" style={{borderBottom:'1px solid var(--border-color)'}}>
        <h1 className="text-2xl font-black" style={{color:'var(--text-primary)'}}>{t('settings.title')}</h1>
      </header>

      <div className="max-w-2xl mx-auto p-8 space-y-3">
        {items.map(({ icon: Icon, label, value, color, onClick }) => (
          <div key={label} onClick={onClick} className={`glass-card-hover p-5 flex items-center justify-between ${onClick ? 'cursor-pointer' : ''}`}>
            <div className="flex items-center gap-4">
              <Icon size={20} className={color} />
              <span className="text-sm font-semibold" style={{color:'var(--text-primary)'}}>{label}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm truncate max-w-[200px]" style={{color:'var(--text-muted)'}}>{value}</span>
              {onClick && <ChevronRight size={16} style={{color:'var(--text-faint)'}} />}
            </div>
          </div>
        ))}

        <div className="pt-6 space-y-3">
          <button onClick={handleLogout} className="w-full glass-card-hover p-5 flex items-center justify-center gap-3 cursor-pointer" style={{color:'var(--text-secondary)'}} data-testid="settings-logout-btn">
            <LogOut size={18} /><span className="text-sm font-semibold">{t('settings.logout')}</span>
          </button>
          <button onClick={() => { setMsg({}); setShowDeleteModal(true); }} className="w-full glass-card-hover p-5 flex items-center justify-center gap-3 text-red-400 hover:bg-red-500/10 cursor-pointer" data-testid="settings-delete-btn">
            <Trash2 size={18} /><span className="text-sm font-semibold">{t('settings.deleteAccount')}</span>
          </button>
        </div>
      </div>

      <Modal open={showLangModal} onClose={() => setShowLangModal(false)} title={t('settings.selectLanguage')}>
        <div className="space-y-1">
          {SUPPORTED_LANGUAGES.map((lang) => (
            <button key={lang.code} onClick={() => { changeLanguage(lang.code); setShowLangModal(false); }}
              className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl transition-all ${i18n.language === lang.code ? 'bg-brand-violet/20 border border-brand-violet/40' : 'hover:bg-[var(--bg-card-hover)]'}`}
              data-testid={`lang-${lang.code}`}
            >
              <div className="flex items-center gap-3">
                <span className="text-lg">{lang.flag === 'GB' ? '\u{1F1EC}\u{1F1E7}' : lang.flag === 'FR' ? '\u{1F1EB}\u{1F1F7}' : lang.flag === 'ES' ? '\u{1F1EA}\u{1F1F8}' : lang.flag === 'DE' ? '\u{1F1E9}\u{1F1EA}' : lang.flag === 'PT' ? '\u{1F1F5}\u{1F1F9}' : lang.flag === 'SA' ? '\u{1F1F8}\u{1F1E6}' : lang.flag === 'IN' ? '\u{1F1EE}\u{1F1F3}' : lang.flag === 'JP' ? '\u{1F1EF}\u{1F1F5}' : lang.flag === 'KR' ? '\u{1F1F0}\u{1F1F7}' : lang.flag === 'NL' ? '\u{1F1F3}\u{1F1F1}' : lang.flag === 'RU' ? '\u{1F1F7}\u{1F1FA}' : lang.flag === 'CN' ? '\u{1F1E8}\u{1F1F3}' : '\u{1F310}'}</span>
                <div className="text-left">
                  <div className="text-sm font-semibold" style={{color:'var(--text-primary)'}}>{lang.nativeName}</div>
                  <div className="text-xs" style={{color:'var(--text-muted)'}}>{lang.name}</div>
                </div>
              </div>
              {i18n.language === lang.code && <Check size={18} className="text-brand-violet" />}
            </button>
          ))}
        </div>
      </Modal>

      <Modal open={showEmailModal} onClose={() => setShowEmailModal(false)} title={t('settings.changeEmail')}>
        {msg.text && <div className={`text-sm rounded-xl px-4 py-3 mb-4 ${msg.type === 'success' ? 'bg-green-500/10 border border-green-500/30 text-green-400' : 'bg-red-500/10 border border-red-500/30 text-red-400'}`}>{msg.text}</div>}
        <form onSubmit={handleChangeEmail} className="space-y-4">
          <div><label className="block text-sm mb-1.5" style={{color:'var(--text-secondary)'}}>{t('auth.email')}</label><input type="email" value={emailForm.new_email} onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })} className="input-field" required data-testid="change-email-input" /></div>
          <div><label className="block text-sm mb-1.5" style={{color:'var(--text-secondary)'}}>{t('auth.password')}</label><PasswordInput value={emailForm.password} onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })} placeholder={t('auth.password')} required data-testid="change-email-password" /></div>
          <button type="submit" disabled={loading} className="w-full py-3 rounded-xl bg-gradient-to-r from-brand-purple to-brand-violet text-white font-bold text-sm disabled:opacity-50" data-testid="change-email-submit">{loading ? t('common.loading') : t('common.save')}</button>
        </form>
      </Modal>

      <Modal open={showPasswordModal} onClose={() => setShowPasswordModal(false)} title={t('settings.changePassword')}>
        {msg.text && <div className={`text-sm rounded-xl px-4 py-3 mb-4 ${msg.type === 'success' ? 'bg-green-500/10 border border-green-500/30 text-green-400' : 'bg-red-500/10 border border-red-500/30 text-red-400'}`}>{msg.text}</div>}
        <form onSubmit={handleChangePassword} className="space-y-4">
          <div><label className="block text-sm mb-1.5" style={{color:'var(--text-secondary)'}}>{t('auth.password')}</label><PasswordInput value={passwordForm.current_password} onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })} placeholder={t('auth.password')} required data-testid="change-password-current" /></div>
          <div><label className="block text-sm mb-1.5" style={{color:'var(--text-secondary)'}}>{t('settings.changePassword')}</label><PasswordInput value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} placeholder="Min. 6 caracteres" minLength={6} required data-testid="change-password-new" /></div>
          <button type="submit" disabled={loading} className="w-full py-3 rounded-xl bg-gradient-to-r from-brand-purple to-brand-violet text-white font-bold text-sm disabled:opacity-50" data-testid="change-password-submit">{loading ? t('common.loading') : t('common.save')}</button>
        </form>
      </Modal>

      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)} title={t('settings.deleteAccount')}>
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4"><p className="text-red-400 text-sm">{t('settings.deleteAccount')}</p></div>
        {msg.text && msg.type === 'error' && <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-xl px-4 py-3 mb-4">{msg.text}</div>}
        <form onSubmit={handleDeleteAccount} className="space-y-4">
          <div><label className="block text-sm mb-1.5" style={{color:'var(--text-secondary)'}}>{t('auth.password')}</label><PasswordInput value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} placeholder={t('auth.password')} required data-testid="delete-password-input" /></div>
          <button type="submit" disabled={loading} className="w-full py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold text-sm disabled:opacity-50" data-testid="delete-account-confirm">{loading ? t('common.loading') : t('common.delete')}</button>
        </form>
      </Modal>
    </div>
  );
}
